import React, { memo } from 'react';
import { format } from 'date-fns';
import { useStoreState } from 'easy-peasy';

import { isMediaQuery } from '~/core/scripts/mediaquery-helper';
import Icon from '~/core/scripts/components/Icon';
import Utils from '~/core/scripts/utils';
import config from '~/config/article';

import * as S from '~/css-in-js/tooltip.styles';

const TOOLTIP_WIDTH = 230;
const TOOLTIP_HEIGHT = 160;
const TOOLTIP_HEIGHT_HALF = TOOLTIP_HEIGHT / 2;

const Tooltip = memo(
  ({ data, pos, isVisible, onClose, showRecoveredCases = true, isMainMap = false }) => {
    const isTrendViewStore = useStoreState((state) => state.app.isTrendView);
    const isTrendView = isMainMap && isTrendViewStore;
    const yOffset = pos.y - TOOLTIP_HEIGHT_HALF < 0 ? 0 : TOOLTIP_HEIGHT_HALF;
    const style = { left: `${pos.x + 45}px`, top: `${pos.y - yOffset}px` };
    const isInverted = pos.x + 45 + TOOLTIP_WIDTH >= window.innerWidth;

    const isClosable = Utils.isTouch || !isMediaQuery(769);

    if (!data) {
      return null;
    }

    const title = data.label;
    const subtitle = data.label_parent;
    const isHistoric =
      format(new Date(data.updated), 'dd.MM.yyyy') !== format(new Date(), 'dd.MM.yyyy') &&
      format(new Date(data.updated), 'HH') === '00';
    const updateTime = data.updated
      ? isHistoric
        ? format(new Date(data.updated), 'dd.MM.yyyy')
        : format(new Date(data.updated), 'dd.MM.yyyy, HH') + ' Uhr'
      : null;
    const yesterday = format(new Date(data.updated) - 1000 * 60 * 60 * 24 * 1, 'dd.MM.yyyy');
    const yesterweek = format(new Date(data.updated) - 1000 * 60 * 60 * 24 * 7, 'dd.MM.yyyy');
    const showCasesPerPop = !!data.population && !!data.confirmed;
    const confirmedPerPop = (data.confirmed * 100000) / data.population;
    const recoveredPerPop = (data.recovered * 100000) / data.population;
    const deathsPerPop = (data.deaths * 100000) / data.population;
    const showAsterisk = config.noRecoveredIds.includes(data.id) && !isTrendView;

    const handleClose = (evt) => {
      evt.preventDefault();
      evt.stopPropagation();

      onClose();
    };

    return (
      <S.Wrapper
        isVisible={isVisible}
        isInverted={isInverted}
        style={{
          ...style,
          borderLeftWidth: 0,
          top: isClosable ? 130 : style.top,
          left: isClosable ? (window.innerWidth - TOOLTIP_WIDTH) / 2 : style.left,
        }}
      >
        <S.Inner>
          {isClosable && (
            <S.Close close onClick={handleClose}>
              <Icon type="close" />
            </S.Close>
          )}
          <S.Header>
            <S.Title>
              {title}
              {showAsterisk ? <sup>*</sup> : ''}
            </S.Title>
            {subtitle && subtitle !== 'null' && <S.SubTitle>{subtitle}</S.SubTitle>}
          </S.Header>
          <S.Body>
            {!isTrendView && <S.Text>Fälle bislang</S.Text>}
            {isTrendView && <S.Text>Fälle letzte 7 Tage</S.Text>}
            <S.ItemWrapper>
              <S.Item type="confirmed">{Utils.numberFormat(data.confirmed)}</S.Item>
              {showRecoveredCases && data.recovered > 0 && !isTrendView && (
                <S.Item type="recovered">{Utils.numberFormat(data.recovered)}</S.Item>
              )}
              {!isTrendView && <S.Item type="death">{Utils.numberFormat(data.deaths)}</S.Item>}
            </S.ItemWrapper>
            {showCasesPerPop && (
              <>
                <S.Text>pro 100.000 Einwohner</S.Text>
                <S.ItemWrapper>
                  <S.Item type="confirmed" isBordered>
                    {Utils.numberFormat(confirmedPerPop)}
                  </S.Item>
                  {showRecoveredCases && data.recovered > 0 && !isTrendView && (
                    <S.Item type="recovered" isBordered>
                      {Utils.numberFormat(recoveredPerPop)}
                    </S.Item>
                  )}
                  {!isTrendView && (
                    <S.Item type="death" isBordered>
                      {Utils.numberFormat(deathsPerPop)}
                    </S.Item>
                  )}
                </S.ItemWrapper>
              </>
            )}
            {showAsterisk && (
              <S.Text>
                <sup>*</sup>Keine offiziellen Angaben zu Genesenen
              </S.Text>
            )}
          </S.Body>
          {isTrendView && (
            <S.Timestamp>
              {yesterweek} - {yesterday}
            </S.Timestamp>
          )}
          {updateTime && !isTrendView && <S.Timestamp>{updateTime}</S.Timestamp>}
        </S.Inner>
      </S.Wrapper>
    );
  }
);

Tooltip.displayName = 'Tooltip';

export default Tooltip;
